import { motion } from "framer-motion";

/**
 * MoneyFlow — streaming animated currency particles drifting across a stage.
 * Used behind the savings-impact numbers to make money "flow".
 *
 * Props:
 *   density       — number of particles (default 28)
 *   color         — particle colour CSS string (default lime)
 *   symbol        — currency symbol (default "£")
 *   className     — wrapper className
 *   testId        — data-testid for the container
 */
export default function MoneyFlow({
  density = 28,
  color = "rgba(150,193,31,0.85)",
  symbol = "£",
  className = "",
  "data-testid": testId,
}) {
  // Pre-seed positions/timing so each particle has its own rhythm
  const particles = Array.from({ length: density }, (_, i) => {
    const startX = (i * 7919) % 100; // pseudo-random spread
    const startY = 100 + ((i * 991) % 30); // start below the stage
    const dx = ((i * 1217) % 60) - 30; // gentle horizontal drift
    const dur = 6 + ((i * 53) % 7); // 6-13s
    const delay = ((i * 271) % 800) / 100; // 0-8s
    const size = 12 + ((i * 31) % 22); // 12-34px
    const opacity = 0.35 + ((i * 13) % 50) / 100; // 0.35-0.85
    return { startX, startY, dx, dur, delay, size, opacity, key: i };
  });

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      data-testid={testId}
      aria-hidden="true"
    >
      {particles.map((p) => (
        <motion.span
          key={p.key}
          className="absolute select-none kn-display"
          style={{
            left: `${p.startX}%`,
            top: `${p.startY}%`,
            color,
            fontSize: p.size,
            fontWeight: 800,
            opacity: 0,
            textShadow: "0 0 18px currentColor",
          }}
          animate={{
            top: [`${p.startY}%`, "-15%"],
            x: [0, p.dx, -p.dx * 0.5, p.dx * 0.7, 0],
            opacity: [0, p.opacity, p.opacity, p.opacity * 0.6, 0],
          }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeOut",
            times: [0, 0.18, 0.65, 0.85, 1],
          }}
        >
          {symbol}
        </motion.span>
      ))}
    </div>
  );
}
